import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import "./BusinessPage.css";

const media = {
  hero: {
    mp4: "/media/hero/hero-video.mp4",
    webm: "/media/hero/hero-video.webm",
    poster: "/media/hero/hero-poster.jpg",
  },
  flow: {
    mp4: "/media/system/system-video.mp4",
    webm: "/media/system/system-video.webm",
    poster: "/media/system/system-poster.jpg",
  },
  showcase: {
    mp4: "/media/showcase/feature-video.mp4",
    webm: "/media/showcase/feature-video.webm",
    poster: "/media/showcase/feature-poster.jpg",
    image: "/media/showcase/gallery-image-01.jpg",
  },
  modules: [
    {
      key: "detect",
      label: "Detect",
      title: "전송 전 프롬프트 검사",
      body:
        "직원이 외부 LLM 입력창에 작성한 내용을 전송 직전에 검사해 개인정보, 계약정보, 내부 코드, 기업 기밀을 탐지합니다.",
      mp4: "/media/manifesto/ai-video.mp4?v=square-20260628",
      webm: "/media/manifesto/ai-video.webm?v=square-20260628",
      poster: "/media/manifesto/ai-poster.jpg?v=square-20260628",
    },
    {
      key: "policy",
      label: "Policy",
      title: "조직별 정책 엔진",
      body:
        "부서, 사용자, 정보 유형별로 다른 보안 정책을 적용하고 고객 전용 실행 환경에서 정책 판단을 수행합니다.",
      mp4: "/media/manifesto/data-video.mp4?v=square-20260628",
      webm: "/media/manifesto/data-video.webm?v=square-20260628",
      poster: "/media/manifesto/data-poster.jpg?v=square-20260628",
    },
    {
      key: "mask",
      label: "Mask",
      title: "마스킹 또는 차단",
      body:
        "위험도가 낮은 정보는 자동 마스킹하고, 기업 정책상 외부 전송이 불가한 정보는 즉시 차단합니다.",
      mp4: "/media/manifesto/automation-video.mp4?v=square-20260628",
      webm: "/media/manifesto/automation-video.webm?v=square-20260628",
      poster: "/media/manifesto/automation-poster.jpg?v=square-20260628",
    },
    {
      key: "audit",
      label: "Audit",
      title: "감사 로그와 관리자 대시보드",
      body:
        "차단 이력, 정책 위반, 마스킹 이벤트를 관리자 대시보드에 기록해 감사와 컴플라이언스 대응에 활용합니다.",
      mp4: "/media/manifesto/speed-video.mp4?v=square-20260628",
      webm: "/media/manifesto/speed-video.webm?v=square-20260628",
      poster: "/media/manifesto/speed-poster.jpg?v=square-20260628",
    },
  ],
};

function BusinessVideo({ slot, className, poster, mp4, webm, labelledBy }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof window === "undefined") {
      return undefined;
    }
    if (window.navigator.userAgent.toLowerCase().includes("jsdom")) {
      return undefined;
    }

    const hydrate = () => {
      if (video.dataset.loaded === "true") {
        const retry = video.play();
        if (retry && typeof retry.catch === "function") {
          retry.catch(() => {});
        }
        return;
      }

      Array.from(video.querySelectorAll("source")).forEach((source) => {
        if (source.dataset.src) {
          source.src = source.dataset.src;
        }
      });
      video.dataset.loaded = "true";
      video.load();
      const attempt = video.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => {});
      }
    };

    if (!("IntersectionObserver" in window)) {
      hydrate();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          hydrate();
          observer.disconnect();
        }
      },
      { rootMargin: "360px 0px" }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      className={className}
      data-media-slot={slot}
      poster={poster}
      preload="none"
      muted
      playsInline
      loop
      autoPlay={false}
      aria-labelledby={labelledBy}
    >
      {webm && <source data-src={webm} type="video/webm" />}
      {mp4 && <source data-src={mp4} type="video/mp4" />}
    </video>
  );
}

export default function BusinessPage() {
  return (
    <main className="business-page">
      <Helmet>
        <title>ZENTO | 기업용 생성형 AI 보안 게이트웨이</title>
        <meta
          name="description"
          content="ZENTO는 ChatGPT, Claude, Gemini 등 외부 LLM에 프롬프트를 보내기 직전 민감정보를 탐지, 마스킹, 차단하고 감사 로그를 남기는 기업용 생성형 AI 보안 게이트웨이입니다."
        />
        <meta
          name="keywords"
          content="ZENTO, ZentoAI, 생성형 AI 보안, AI 보안 게이트웨이, ChatGPT 보안, LLM 보안, 프롬프트 보안, AI DLP, 민감정보 마스킹, 감사 로그"
        />
        <link rel="canonical" href="https://zento.im/business" />
      </Helmet>

      <section className="business-hero" aria-labelledby="business-hero-title">
        <BusinessVideo
          slot="business-hero-video"
          className="business-hero-video"
          poster={media.hero.poster}
          mp4={media.hero.mp4}
          webm={media.hero.webm}
          labelledBy="business-hero-title"
        />
        <div className="business-hero-overlay" />
        <div className="business-hero-copy">
          <p className="business-eyebrow">ZENTO AI Security Gateway</p>
          <h1 id="business-hero-title">
            생성형 AI를 막지 말고, 전송 직전에 통제하세요.
          </h1>
          <p>
            ZENTO는 외부 LLM 입력창 위에서 민감정보를 탐지하고, 기업 정책에 따라
            마스킹, 차단, 감사 로그를 남기는 브라우저 기반 AI 보안 게이트웨이입니다.
          </p>
          <div className="business-hero-actions">
            <a className="business-primary-action" href="mailto:ovo@zento.im">
              PoC 문의
            </a>
            <a
              className="business-secondary-action"
              href="https://www.youtube.com/watch?v=C-yktg-pdpQ"
              target="_blank"
              rel="noopener noreferrer"
            >
              솔루션 영상
            </a>
          </div>
        </div>
      </section>

      <section className="business-problem" aria-labelledby="business-problem-title">
        <div className="business-section-heading">
          <p className="business-eyebrow">Problem</p>
          <h2 id="business-problem-title">
            공지와 교육만으로는 프롬프트 유출을 막을 수 없습니다.
          </h2>
          <p>
            직원들은 이미 ChatGPT, Claude, Gemini 같은 외부 LLM을 업무에 사용합니다.
            문제는 고객정보, 계약조건, 내부 코드, 개인정보가 입력창에서 외부로 넘어가는
            바로 그 순간입니다.
          </p>
        </div>
        <div className="business-problem-grid">
          <article>
            <span>01</span>
            <h3>사용 금지는 우회 사용을 만듭니다.</h3>
            <p>
              AI 활용을 막으면 생산성이 떨어지고, 개인 계정이나 비공식 경로 사용이
              늘어납니다.
            </p>
          </article>
          <article>
            <span>02</span>
            <h3>기존 DLP는 이메일과 파일 중심입니다.</h3>
            <p>
              짧고 빈번한 LLM 프롬프트 입력을 전송 직전에 판단하는 데에는 공백이
              남습니다.
            </p>
          </article>
          <article>
            <span>03</span>
            <h3>감사는 전송 이후에 시작됩니다.</h3>
            <p>
              ZENTO는 사후 확인이 아니라, 외부 AI 서비스로 나가기 직전의 통제 지점을
              만듭니다.
            </p>
          </article>
        </div>
      </section>

      <section className="business-flow" aria-labelledby="business-flow-title">
        <div className="business-flow-media">
          <BusinessVideo
            slot="business-flow-video"
            className="business-flow-video"
            poster={media.flow.poster}
            mp4={media.flow.mp4}
            webm={media.flow.webm}
            labelledBy="business-flow-title"
          />
        </div>
        <div className="business-flow-copy">
          <p className="business-eyebrow">How It Works</p>
          <h2 id="business-flow-title">브라우저 입력 단계에서 작동하는 보안 게이트웨이.</h2>
          <ol>
            <li>
              <strong>Prompt Capture</strong>
              <span>외부 LLM 입력창에 작성된 프롬프트를 전송 직전에 감지합니다.</span>
            </li>
            <li>
              <strong>Policy Check</strong>
              <span>조직 정책과 온프레미스 AI 기반 정책 엔진으로 위험 정보를 판별합니다.</span>
            </li>
            <li>
              <strong>Mask or Block</strong>
              <span>민감정보는 마스킹하고, 전송 불가 정보는 차단합니다.</span>
            </li>
            <li>
              <strong>Audit Trail</strong>
              <span>관리자 대시보드에 차단 이력과 정책 이벤트를 기록합니다.</span>
            </li>
          </ol>
        </div>
      </section>

      <section className="business-modules" aria-labelledby="business-modules-title">
        <div className="business-section-heading">
          <p className="business-eyebrow">Product</p>
          <h2 id="business-modules-title">안전한 AI 사용 허용을 위한 네 가지 모듈.</h2>
        </div>
        <div className="business-module-grid">
          {media.modules.map((item) => (
            <article className="business-module" key={item.key}>
              <BusinessVideo
                slot={`business-${item.key}-video`}
                className="business-module-video"
                poster={item.poster}
                mp4={item.mp4}
                webm={item.webm}
                labelledBy={`business-${item.key}-title`}
              />
              <div>
                <p>{item.label}</p>
                <h3 id={`business-${item.key}-title`}>{item.title}</h3>
                <span>{item.body}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="business-architecture" aria-labelledby="business-architecture-title">
        <div className="business-architecture-copy">
          <p className="business-eyebrow">Deployment</p>
          <h2 id="business-architecture-title">
            기존 LLM 계정과 구독 구조를 바꾸지 않고 시작합니다.
          </h2>
          <p>
            Chrome 확장 프로그램 기반 MVP로 부서 단위 PoC를 빠르게 시작하고,
            고객별 실행 환경, 전용 VPC, 온프레미스, SIEM 연동까지 확장할 수 있습니다.
          </p>
        </div>
        <div className="business-architecture-stack" aria-label="ZENTO deployment architecture">
          <span>Chrome Extension</span>
          <span>Policy Engine</span>
          <span>Masking / Blocking</span>
          <span>Audit Log</span>
          <span>Admin Dashboard</span>
          <span>Enterprise VPC / On-prem</span>
        </div>
      </section>

      <section className="business-proof" aria-labelledby="business-proof-title">
        <div className="business-section-heading">
          <p className="business-eyebrow">Validation</p>
          <h2 id="business-proof-title">현장에서 확인한 구매 가능성.</h2>
        </div>
        <div className="business-proof-grid">
          <article>
            <strong>Security Interviews</strong>
            <p>
              대기업 정보보안 담당자, ISO 심사원, 대학병원 정보보안 담당자,
              AI 의료 스타트업 보안 책임자 인터뷰로 문제를 검증했습니다.
            </p>
          </article>
          <article>
            <strong>Paid PoC Pipeline</strong>
            <p>
              AI 의료기업과 하반기 중 보안팀 및 일부 부서를 대상으로 한 유료 PoC를
              협의했습니다.
            </p>
          </article>
          <article>
            <strong>Real Security Experience</strong>
            <p>
              실제 운영 서비스에서 개인정보와 창업 아이디어 관련 정보가 노출될 수 있는
              취약점을 발견하고 공식 채널로 사전 제보했습니다.
            </p>
          </article>
        </div>
      </section>

      <section className="business-team-proof" aria-labelledby="business-team-title">
        <div>
          <p className="business-eyebrow">Team</p>
          <h2 id="business-team-title">AI 보안을 제품과 매출로 바꾸는 팀.</h2>
          <p>
            Enterprise PoC, AI 보안 테스트, SaaS 제품 경험, 백엔드 로그 인프라,
            GTM을 한 팀 안에서 동시에 실행합니다.
          </p>
        </div>
        <img
          src={media.showcase.image}
          alt="ZENTO 팀과 제품 실행을 상징하는 이미지"
          loading="lazy"
        />
      </section>

      <section className="business-cta" aria-labelledby="business-cta-title">
        <p className="business-eyebrow">PoC</p>
        <h2 id="business-cta-title">외부 LLM 사용을 막지 않고 통제하는 첫 PoC를 시작하세요.</h2>
        <a className="business-primary-action" href="mailto:ovo@zento.im">
          ovo@zento.im
        </a>
        <p>(주)OvO.Inc</p>
      </section>
    </main>
  );
}
